import { useMutation } from "@apollo/client";
import { Feather } from "@expo/vector-icons";
import { Q } from "@nozbe/watermelondb";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  Card,
  Divider,
  Page,
  PayTypeToIcon,
  Button,
  useThemeProvider,
} from "@towersystems/roam-common-native";
import { Terminal } from "@towersystems/roam-common/lib/generated-types";
import { isEmpty, xor } from "lodash";
import { FlatList } from "react-native";
import { TerminalSettingsRootParamList } from "../../../../types";
import PaymentMethod from "../../../../utilities/data/model/PaymentMethod";
import { useTerminalModelContextProvider } from "../../../../utilities/terminal";
import { MUTATION_UPDATE_TERMINAL } from "../../../../utilities/terminal/constants";
import { SelectablePaymentMethodButton } from "../../../point-of-sale/screens/SelectPaymentMethodScreen/components/SelectablePaymentMethodButton";
import { useGlobalSettingsProvider } from "../../../../utilities/global-settings/hooks";
import React from "react";
import { useTranslation } from "../../../../utilities/i18n";
import { Colors } from "react-native-ui-lib";
import { useFocusEffect } from "@react-navigation/native";

export type SelectTerminalPaymentMethodsScreenProps = NativeStackScreenProps<
  TerminalSettingsRootParamList,
  "SelectTerminalPaymentMethodsScreen"
>;

export const SelectTerminalPaymentMethodsScreen = ({
  navigation,
}: SelectTerminalPaymentMethodsScreenProps) => {
  const { t } = useTranslation("settings");
  const { colors } = useThemeProvider();
  const { terminal } = useTerminalModelContextProvider();
  const database = useDatabase();
  const [paymentMethods, setPaymentMethods] = React.useState<PaymentMethod[]>(
    []
  );
  const globalSettings = useGlobalSettingsProvider();

  const [selectedPaymentMethods, setSelectedPaymentMethods] = React.useState<
    string[]
  >([]);

  const [f] = useMutation<{ updateTerminal: Terminal }>(
    MUTATION_UPDATE_TERMINAL
  );

  useFocusEffect(
    React.useCallback(() => {
      database
        .get<PaymentMethod>("payment_methods")
        .query(
          Q.where("enabled", true),
          Q.on("pay_types", "internal", Q.eq(false)),
          Q.on("pay_types", "available", Q.eq(true)),
          Q.where("deleted_at", Q.eq(null))
        )
        .fetch()
        .then(setPaymentMethods)
        .catch((e) => console.log(e));
    }, [])
  );

  React.useEffect(() => {
    if (paymentMethods) {
      const selectedPaymentMethodIds = terminal.paymentMethodIds.filter((id) =>
        paymentMethods.find((pm) => pm.id === id)
      );
      setSelectedPaymentMethods(selectedPaymentMethodIds);
    }
  }, [paymentMethods]);

  const isDirty = React.useMemo(() => {
    return !isEmpty(xor(terminal.paymentMethodIds, selectedPaymentMethods));
  }, [terminal.paymentMethodIds, selectedPaymentMethods]);

  const handleOnSave = async () => {
    return f({
      variables: {
        input: {
          id: terminal.id,
          paymentMethodIds: selectedPaymentMethods,
        },
      },
    })
      .then((r) => {
        if (r.data) {
          return database.write(async () => {
            await terminal.update((t) => {
              t.paymentMethodIds =
                r.data?.updateTerminal.paymentMethodIds || [];
            });
          });
        }
      })
      .then(() => navigation.goBack())
      .catch(console.log);
  };

  const addPaymentMethod = (paymentMethodId: string) => {
    if (false == selectedPaymentMethods.includes(paymentMethodId)) {
      setSelectedPaymentMethods([...selectedPaymentMethods, paymentMethodId]);
    }
  };

  const removePaymentMethod = (paymentMethodId: string) => {
    if (true == selectedPaymentMethods.includes(paymentMethodId)) {
      setSelectedPaymentMethods(
        selectedPaymentMethods.filter((i) => i !== paymentMethodId)
      );
    }
  };

  const renderItem = ({ item }: { item: PaymentMethod }) => {
    const selected =
      selectedPaymentMethods.includes(item.id) ||
      globalSettings.cashManagedPaymentMethodId === item.id;
    return (
      <SelectablePaymentMethodButton
        disabled={globalSettings.cashManagedPaymentMethodId === item.id}
        onPress={() =>
          selected ? removePaymentMethod(item.id) : addPaymentMethod(item.id)
        }
        label={item.name}
        selected={selected}
        icon={<PayTypeToIcon payType={item.payType.id} />}
        componentRight={() => {
          return selected ? (
            <Feather name="check" size={24} color={colors.primary} />
          ) : null;
        }}
      />
    );
  };

  const disabled = !isDirty;

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.update_payment_methods")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleOnSave}
    ></Button>
  );

  return (
    <Page
      title={t("select_terminal_payment_methods_title")}
      secondaryTitle={t("select_terminal_payment_methods_description")}
      footer={footerMarkup}
    >
      <FlatList
        ItemSeparatorComponent={Divider}
        renderItem={renderItem}
        data={paymentMethods}
        scrollEnabled={true}
      ></FlatList>
    </Page>
  );
};
